import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/context/LanguageContext';
import PickupDiscountBanner from '@/components/PickupDiscountBanner';

const CartDrawer = () => {
  const navigate = useNavigate();
  const { cart, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, cartTotal, isLoading } = useCart();
  const { t } = useLanguage();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-lg bg-[#FDFBF7] border-l border-[#E8DEC8]" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[#2D231E] flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-[#C44536]" />
            {t('cart_title')}
          </SheetTitle>
        </SheetHeader>

        {cart.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center">
            <ShoppingBag className="w-16 h-16 text-[#E8DEC8] mb-4" />
            <h3 className="font-serif text-xl text-[#2D231E] mb-2">{t('cart_empty')}</h3>
            <p className="font-sans text-sm text-[#5C4D43] mb-4">{t('cart_empty_desc')}</p>
            <Button onClick={() => setIsCartOpen(false)} className="btn-primary" data-testid="continue-shopping-empty">{t('cart_view_menu')}</Button>
          </div>
        ) : (
          <>
            <div className="mt-4 pr-4">
              <PickupDiscountBanner compact />
            </div>
            <ScrollArea className="h-[calc(100vh-320px)] mt-4 pr-4">
              <div className="space-y-4">
                {cart.items.map((item) => (
                  <div key={item.menu_item_id} className="flex gap-4 p-4 bg-[#F5EFE6] rounded-xl" data-testid={`cart-item-${item.menu_item_id}`}>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif font-semibold text-[#2D231E] whitespace-pre-line leading-tight">{item.name}</h4>
                      <p className="font-sans text-sm text-[#C44536] font-medium">{item.price.toFixed(2)} EUR</p>
                      {item.spiciness && <p className="font-sans text-xs text-[#E28743]">{item.spiciness}</p>}
                      <div className="flex items-center gap-3 mt-2">
                        <button onClick={() => updateQuantity(item.menu_item_id, item.quantity - 1)} disabled={isLoading} className="qty-btn bg-[#E8DEC8] hover:bg-[#C44536] hover:text-[#FDFBF7]" data-testid={`decrease-qty-${item.menu_item_id}`}><Minus className="w-4 h-4" /></button>
                        <span className="font-sans font-medium text-[#2D231E] w-6 text-center">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.menu_item_id, item.quantity + 1)} disabled={isLoading} className="qty-btn bg-[#E8DEC8] hover:bg-[#C44536] hover:text-[#FDFBF7]" data-testid={`increase-qty-${item.menu_item_id}`}><Plus className="w-4 h-4" /></button>
                        <button onClick={() => removeFromCart(item.menu_item_id)} disabled={isLoading} className="ml-auto text-[#C44536] hover:text-[#A83A2D] transition-colors" data-testid={`remove-item-${item.menu_item_id}`}><Trash2 className="w-5 h-5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-[#FDFBF7] border-t border-[#E8DEC8]">
              <Separator className="mb-4 bg-[#E8DEC8]" />
              <div className="flex justify-between items-center mb-6">
                <span className="font-serif text-lg font-semibold text-[#2D231E]">{t('cart_total')}</span>
                <span className="font-serif text-2xl font-bold text-[#C44536]">{cartTotal.toFixed(2)} EUR</span>
              </div>
              <SheetFooter>
                <Button onClick={handleCheckout} disabled={cart.items.length === 0} className="btn-primary w-full py-6 text-lg" data-testid="checkout-button">{t('cart_checkout')}</Button>
              </SheetFooter>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
